import { flushSync } from 'react-dom';
import { Moon, Sun } from 'lucide-react';
import type { PressEvent } from 'react-aria-components';
import { Button } from '@/shared/ui/button';
import { useTheme } from '@/app/providers/theme-provider';

export function ThemeToggle() {
  const { theme, setTheme } = useTheme();

  const toggleTheme = (event: PressEvent) => {
    const nextTheme = theme === 'light' ? 'dark' : 'light';

    const prefersReducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Fallback: browsers without the View Transitions API (and users who
    // prefer reduced motion) get the plain instant switch.
    if (
      typeof document.startViewTransition !== 'function' ||
      prefersReducedMotion
    ) {
      setTheme(nextTheme);
      return;
    }

    // Circular reveal originating on the toggle (see .theme-transition rules
    // in styles.css). The class scopes the clip-path animation so it cannot
    // fight the route-transition ::view-transition styles.
    //
    // Measure the button rather than read the pointer: PressEvent carries no
    // viewport coordinates (its x/y are target-relative, and 0 for keyboard
    // activation), and keying off the element means the reveal starts from
    // the same place for mouse, touch and keyboard alike.
    const root = document.documentElement;
    const rect = event.target.getBoundingClientRect();
    root.style.setProperty('--theme-x', `${rect.left + rect.width / 2}px`);
    root.style.setProperty('--theme-y', `${rect.top + rect.height / 2}px`);
    root.classList.add('theme-transition');

    const transition = document.startViewTransition(() => {
      // flushSync so the theme class swap is committed before the browser
      // captures the "new" snapshot.
      flushSync(() => {
        setTheme(nextTheme);
      });
    });

    void transition.finished.finally(() => {
      root.classList.remove('theme-transition');
      root.style.removeProperty('--theme-x');
      root.style.removeProperty('--theme-y');
    });
  };

  return (
    <Button variant="ghost" size="icon" onPress={toggleTheme}>
      <Sun className="h-[1.2rem] w-[1.2rem] scale-100 rotate-0 transition-transform duration-200 dark:scale-0 dark:-rotate-90" />
      <Moon className="absolute h-[1.2rem] w-[1.2rem] scale-0 rotate-90 transition-transform duration-200 dark:scale-100 dark:rotate-0" />
    </Button>
  );
}
